/**
 * «Добавить в договор» под товарами (право `contract_edit`, **пишет в боевую CRM**): подбор
 * `ProductPicker`, затем каждый выбранный — отдельной записью по очереди (`addPickedItems`: каждая со
 * свежей версией договора — из ответа прошлой записи, иначе перечитать). Прогресс «2 из 3»; на ошибке останавливаемся
 * и оставляем в подборе то, что не добавилось. Предупреждения ответа (пересечения с бронями) — после.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ProductPicker } from "@/components/contract/ProductPicker";
import { type PickedItem, addPickedItems, pickedLabel, useContractRefresh, writeError } from "@/lib/contract";
import { btn } from "@/lib/panelBtn";

export function AddProduct({
  dealId,
  dogovorId,
  chatId,
  version,
  rentStart,
  rentEnd,
  exclude,
  initial = [],
  onTaken,
}: {
  dealId: number;
  dogovorId: number;
  chatId: number | null;
  /** Версия договора, который видит сотрудник (`GET /api/contracts/{id}`). */
  version: string;
  rentStart: string | null;
  rentEnd: string | null;
  /** Серии живых строк договора. */
  exclude: number[];
  /** Выбранное при создании договора, что не добавилось: подбор открыт сразу с ним. */
  initial?: PickedItem[];
  /** Подбор взял `initial` — вызывающий его забывает: повторный монтаж не откроет то же ещё раз. */
  onTaken?: () => void;
}) {
  const qc = useQueryClient();
  const refresh = useContractRefresh(dealId, dogovorId, chatId);
  const [open, setOpen] = useState(initial.length > 0);
  const [items, setItems] = useState<PickedItem[]>(initial);
  // Новый ключ — подбор заново (после записи — с тем, что не добавилось).
  const [round, setRound] = useState(0);
  const [busy, setBusy] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [added, setAdded] = useState(0);
  // Только при монтаже: `initial` уже в состоянии подбора.
  useEffect(() => {
    if (initial.length > 0) onTaken?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    const list = items;
    setError(null);
    setWarnings([]);
    setAdded(0);
    const r = await addPickedItems(dogovorId, list, version, chatId, qc, (done, total) => setBusy({ done, total }));
    if (r.error !== null) {
      const why = writeError(r.error);
      setError(
        r.reading
          ? `Добавлено ${r.done} из ${list.length}, дальше не идём: договор не перечитался — ${why}`
          : `Добавлено ${r.done} из ${list.length}. «${pickedLabel(list[r.done]!)}» не добавлен: ${why}`,
      );
    }
    setBusy(null);
    setWarnings(r.warnings);
    setAdded(r.done);
    const rest = list.slice(r.done);
    setItems(rest);
    setRound((n) => n + 1);
    if (rest.length === 0) setOpen(false);
    // И после ошибки: договор мог измениться — перечитать и показать как есть.
    refresh();
  };

  const report = (
    <>
      {added > 0 && !open && !error && <div className="text-ok">Добавлено в договор: {added}.</div>}
      {error && <div className="whitespace-pre-wrap text-err">{error}</div>}
      {warnings.length > 0 && (
        <ul className="flex flex-col text-[12px] font-medium text-err">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
    </>
  );
  if (!open)
    return (
      <div className="flex flex-col gap-1 text-[12.5px]">
        {report}
        <button
          type="button"
          onClick={() => (setOpen(true), setError(null), setWarnings([]), setAdded(0))}
          className="self-start text-[12px] text-accent-text hover:underline"
        >
          + добавить товар или услугу
        </button>
      </div>
    );
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-ui)] border border-border bg-bg p-2 text-[12.5px]" data-annot={`добавить в договор ${dogovorId}`}>
      <div className="font-medium">Добавить в договор</div>
      <ProductPicker
        key={round}
        chatId={chatId}
        dealId={dealId}
        rentStart={rentStart}
        rentEnd={rentEnd}
        exclude={exclude}
        initial={items}
        disabled={busy != null}
        onChange={setItems}
      />
      {report}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btn.primary} disabled={items.length === 0 || busy != null} onClick={() => void run()}>
          {busy ? `Записываю в CRM… ${busy.done + 1} из ${busy.total}` : items.length > 1 ? `Добавить (${items.length})` : "Добавить"}
        </button>
        <button type="button" className={btn.quiet} disabled={busy != null} onClick={() => (setOpen(false), setItems([]), setRound((n) => n + 1))}>
          Закрыть
        </button>
        {items.length === 0 && !busy && <span className="text-[11.5px] text-fg-muted">Отметьте товар или услугу</span>}
      </div>
    </div>
  );
}
